import React, { useEffect, useMemo, useState } from 'react';
import {
    DataGridPro,
    GridToolbarColumnsButton,
    GridToolbarContainer, GridToolbarExport,
    GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';
import Box from '@mui/material/Box';
import OrgApiService from '../api/OrgApiService';
import EditMenuButton from './Modal/EditMenuButton';
import Typography from '@mui/material/Typography';
import { Button, FormControl } from '@mui/material';
import EditOrganizationModal from './Modal/Admin/EditOrganizationModal';
import ConfirmDeleteDialogHazard from './Modal/ConfirmDeleteHazard';
import apiService from '../api/apiService';
import { useTranslation } from 'react-i18next';

const orgApiService = new OrgApiService();

const Organizations = () => {
    const { t } = useTranslation();
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(100);
    const [currentSelectedOrganization, setCurrentSelectedOrganization] = useState();
    const [openEditOrganizationModal, setOpenEditOrganizationModal] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    function CustomToolbar() {
        return (
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
            </GridToolbarContainer>
        );
    }

    const columns = useMemo(() => [
        {
            field: 'ogId',
            headerName: t('common.id'),
            flex: 0.5,
        },
        {
            field: 'ogName',
            headerName: t('fieldName.name'),
            flex: 1,
        },
        {
            field: 'ogActive',
            headerName: t('common.active'),
            flex: 0.5,
            renderCell: (params) => {
                if (params.row.ogActive > 0) {
                    return t('common.yes');
                } else {
                    return t('common.no');
                }
            },
        },
        {
            field: 'ogCanSearch',
            headerName: t('organizationsPage.column.canSearch'),
            flex: 0.5,
            renderCell: (params) => {
                if (params.row.ogCanSearch > 0) {
                    return t('common.yes');
                } else {
                    return t('common.no');
                }
            },
        },
        {
            field: 'OgNonAdminsCanUse',
            headerName: t('organizationsPage.column.nonAdminUse'),
            flex: 0.5,
            renderCell: (params) => {
                if (params.row.OgNonAdminsCanUse > 0) {
                    return t('common.yes');
                } else {
                    return t('common.no');
                }
            },
        },
        {
            field: 'OgCanOnlySeeOwnReported',
            headerName: t('organizationsPage.column.seeOnlyOwn'),
            flex: 0.5,
            renderCell: (params) => {
                if (params.row.OgCanOnlySeeOwnReported > 0) {
                    return t('common.yes');
                } else {
                    return t('common.no');
                }
            },
        },
        {
            field: 'ogOtherOrgsPermissionLevel',
            headerName: t('organizationsPage.column.otherOrgPermissionLevel'),
            flex: 1,
            renderCell: (params) => {
                if (params.row.ogOtherOrgsPermissionLevel === 0) {
                    return t('permissionLevel.none');
                } else if (params.row.ogOtherOrgsPermissionLevel === 1) {
                    return t('organizationsPage.permissionLevel.view');
                } else if (params.row.ogOtherOrgsPermissionLevel === 2) {
                    return t('organizationsPage.permissionLevel.addEdit');
                }
            },
        },
        {
            field: 'OgExternalUser',
            headerName: t('organizationsPage.column.external'),
            flex: 0.5,
            renderCell: (params) => {
                if (params.row.OgExternalUser > 0) {
                    return t('common.yes');
                } else {
                    return t('common.no');
                }
            },
        },
        {
            field: 'ogCanBeAssignedTo',
            headerName: t('organizationsPage.column.canBeAssigned'),
            flex: 0.5,
            renderCell: (params) => {
                if (params.row.ogCanBeAssignedTo > 0) {
                    return t('common.yes');
                } else {
                    return t('common.no');
                }
            },
        },
        {
            field: 'ogStatusFieldPermissionLevel',
            headerName: t('organizationsPage.column.statusPermissionLevel'),
            flex: 1,
            renderCell: (params) => {
                if (params.row.ogStatusFieldPermissionLevel === 0) {
                    return t('permissionLevel.none');
                } else if (params.row.ogStatusFieldPermissionLevel === 1) {
                    return t('organizationsPage.permissionLevel.view');
                } else if (params.row.ogStatusFieldPermissionLevel === 2) {
                    return t('organizationsPage.permissionLevel.addEdit');
                }
            },
        },
        {
            field: 'ogAssignedToFieldPermissionLevel',
            headerName: t('organizationsPage.column.assignedToPermissionLevel'),
            flex: 1,
            renderCell: (params) => {
                if (params.row.ogAssignedToFieldPermissionLevel === 0) {
                    return t('permissionLevel.none');
                } else if (params.row.ogAssignedToFieldPermissionLevel === 1) {
                    return t('organizationsPage.permissionLevel.view');
                } else if (params.row.ogAssignedToFieldPermissionLevel === 2) {
                    return t('organizationsPage.permissionLevel.addEdit');
                }
            },
        },
        {
            field: 'ogPriorityFieldPermissionLevel',
            headerName: t('organizationsPage.column.priorityPermissionLevel'),
            flex: 1,
            renderCell: (params) => {
                if (params.row.ogPriorityFieldPermissionLevel === 0) {
                    return t('permissionLevel.none');
                } else if (params.row.ogPriorityFieldPermissionLevel === 1) {
                    return t('organizationsPage.permissionLevel.view');
                } else if (params.row.ogPriorityFieldPermissionLevel === 2) {
                    return t('organizationsPage.permissionLevel.addEdit');
                }
            },
        },
        {
            field: 'ogDomain',
            headerName: t('organizationsPage.column.domain'),
            flex: 1,
        },
        {
            field: 'edit',
            headerName: t('common.edit'),
            flex: 0.5,
            filterable: false,
            sortable: false,
            renderCell: (params) => {
                return <EditMenuButton
                    params={params}
                    setShowDeleteDialog={setShowDeleteDialog}
                    setCurrentSelectedRecord={setCurrentSelectedOrganization}
                    setShowEditDialog={setOpenEditOrganizationModal}
                    onEditFunc={() => {
                        setOpenEditOrganizationModal(true);
                        setCurrentSelectedOrganization(params.row);
                    }}
                />;
            },
        },
    ], [t]);

    async function populateOrganizations () {
        const response = await orgApiService.getOrgs();
        if (response != null && response.success) {
            setOrganizations(response.data);
        }
    }

    function onDeleteOrganizationClick (deleteValue) {
        if (deleteValue) {
            apiService.deleteData('api/Organization/' + currentSelectedOrganization.ogId).then(async () => {
                await populateOrganizations();
            });
        }

        setShowDeleteDialog(false);
    };

    const dialogTitle = () => {
        return t('organizationsPage.confirmDeleteTitle', { name: currentSelectedOrganization?.ogName ?? '' });
    };

    const dialogLabel = () => {
        return t('organizationsPage.confirmDeleteLabel', { name: currentSelectedOrganization?.ogName ?? '' });
    };

    useEffect(() => {
        async function setupOrganizations () {
            setLoading(true);
            await populateOrganizations();
            setLoading(false);
        }

        setupOrganizations();
    }, []);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - var(--ct-header-h, 56px))' }}>
            <Typography variant="h4" sx={{ marginBottom: '16px' }}>
                {t('organizationsPage.title')}
            </Typography>
            <FormControl sx={{ marginBottom: '8px' }}>
                <Button size={'large'} variant="contained" onClick={() => setOpenEditOrganizationModal(true)} >{t('organizationsPage.createButton')}</Button>
            </FormControl>
            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                <DataGridPro
                    rows={organizations}
                    columns={columns}
                    loading={loading}
                    pagination
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    getRowId={(row) => row.ogId}
                    components={{
                        Toolbar: CustomToolbar,
                    }}
                    getRowHeight={() => 'auto'}
                    rowsPerPageOptions={[10, 25, 50, 100, 200]}
                />
            </Box>
            <EditOrganizationModal
                open={openEditOrganizationModal}
                currentOrganization={currentSelectedOrganization}
                onCancel={() => {
                    setOpenEditOrganizationModal(false);
                    setCurrentSelectedOrganization(null);
                }}
                onSave={async () => {
                    await populateOrganizations();
                    setOpenEditOrganizationModal(false);
                    setCurrentSelectedOrganization(null);
                }}
            />
            <ConfirmDeleteDialogHazard
                id="confirm-delete"
                keepMounted
                value={currentSelectedOrganization}
                open={showDeleteDialog}
                onClose={onDeleteOrganizationClick}
                deleteTitle={dialogTitle()}
                deleteLabel={dialogLabel()}
            ></ConfirmDeleteDialogHazard>
        </Box>
    );
};
export default Organizations;
